import React, { useEffect } from 'react';

import * as Sentry from '@sentry/browser';

import { Box, COLORS, Dropdown, Label, Text } from '@clutter/clean';

import {
  AddressFragment,
  DoorToDoor__BundleEnum,
  PlanFragment,
  RateGroupFragment,
  RateGroupKindEnum,
} from '@graphql/platform';

import { StorageCheckoutStepProps } from '@root/components/checkout/types';
import { BOX_PLAN, DIMENSIONAL_PLANS } from '@root/constants/pricing';
import { EventSchema } from '@root/resources/wt/types';

import { Currency } from '@shared/currency';

import { useStoragePriceCompletedFunnelEvent } from '@utils/hooks/funnel_events/use_storage_price_completed_funnel_event';
import { useTrackFunnelEvents } from '@utils/hooks/funnel_events/use_track_funnel_event';
import { usePricingSetForZIP } from '@utils/hooks/pricing';

const DEFAULT_QUANTITY = 1;

const MONTHLY_SUFFIX = '/mo';

let hasScrolled = false;

type PlanSummary = {
  plan: PlanFragment;
  amount: number;
  rateGroup: RateGroupFragment;
};

type PlanSizeStepProps = StorageCheckoutStepProps & {
  movingExtendedDestinationServiceArea?: boolean;
  endAddress?: Partial<AddressFragment>;
};

export const PlanSize: React.FC<PlanSizeStepProps> = ({
  onChange,
  scrollToStep,
  values: {
    planSize,
    zip,
    commitment = RateGroupKindEnum.Saver,
    skipStoragePlanSizeSelected,
    currentStorageCustomer,
  },
  movingExtendedDestinationServiceArea,
  endAddress,
}) => {
  const trackFunnelEvent = useTrackFunnelEvents();
  const trackStoragePriceCompleted = useStoragePriceCompletedFunnelEvent();

  const onSelect = (size?: any) => {
    const amount = planMap?.[size].amount;
    if (amount) {
      trackStoragePriceCompleted({
        planSize: size,
        commitment,
        amount,
      });
    } else {
      Sentry.captureMessage(
        `Selected plan ${size} without prices`,
        Sentry.Severity.Warning,
      );
    }
    if (!hasScrolled) {
      hasScrolled = true;
      scrollToStep('next');
    }

    onChange('planQuantity', DEFAULT_QUANTITY);
    onChange('planSize', size);
    onChange(
      'bundleKind',
      size === BOX_PLAN.name ? DoorToDoor__BundleEnum.Basic : undefined,
    );
  };

  const storagePricingGroupEntries = usePricingSetForZIP(
    endAddress?.zip || zip,
  )?.storagePricingGroupEntries;

  const planMap = storagePricingGroupEntries
    ?.filter((pge) => pge.rateGroup?.name === commitment)
    ?.reduce<Record<string, PlanSummary>>((acc, pge) => {
      acc[pge.pricing.plan.name] = {
        plan: pge.pricing.plan,
        amount: pge.pricing.amount,
        rateGroup: pge.rateGroup!,
      };
      return acc;
    }, {});

  const visible =
    skipStoragePlanSizeSelected === false &&
    !currentStorageCustomer &&
    !movingExtendedDestinationServiceArea;

  useEffect(() => {
    visible &&
      storagePricingGroupEntries &&
      storagePricingGroupEntries.length > 0 &&
      trackFunnelEvent({
        schema: EventSchema.WWW__StoragePriceViewed,
        action: 'display',
        metadata: {},
      });
  }, [visible, storagePricingGroupEntries, trackFunnelEvent]);

  if (!visible) {
    return null;
  }

  const options = DIMENSIONAL_PLANS.map((plan) => {
    const planSummary = planMap?.[plan.name];
    return {
      label: (
        <>
          {plan.name}{' '}
          {planSummary && (
            <>
              (First month free, then <Currency amount={planSummary.amount} />
              {MONTHLY_SUFFIX})
            </>
          )}
        </>
      ),
      value: plan.name,
    };
  });

  return (
    <Box margin={['48px 0 0', null, '72px 0 0']}>
      <Box>
        <Text.Title size="small" color={COLORS.tealDark}>
          How much storage do you think you’ll need?
        </Text.Title>
      </Box>
      <Box margin="4px 0 0">
        <Text.Body color={COLORS.storm}>
          Choose a storage size for your items. Once we have picked up and
          inventoried them, we will automatically downsize you to the
          appropriate plan so you only pay for what you use.
        </Text.Body>
      </Box>
      <Box margin="16px 0 0">
        <Label>Storage size</Label>
        <Dropdown
          value={planSize}
          options={options}
          placeholder="Select a storage size"
          onChange={onSelect}
        />
      </Box>
    </Box>
  );
};
